class BLEContext {
    constructor() {
        this.state = {};
        this.writeValues = {};
        this.characteristicsByName = {};
        this.characteristicsByService = {};
        this.characteristics = [];
        this.device = null;
        this.cleanupRunning = false;
        this.timer = null;
        this.onStateChange = () => {};
        this.onProgressChange = () => {};
        this.onDisconnect = () => {};
    }

    configure(config) {
        this.characteristicsByName[config.name] = config;
        if (this.characteristicsByService[config.serviceUUID] == null) {
            this.characteristicsByService[config.serviceUUID] = [];
        }
        this.characteristicsByService[config.serviceUUID].push(this.characteristicsByName[config.name]);
        this.characteristics.push(this.characteristicsByName[config.name]);
    }

    setStateValue(name, value, readType, changeType) {
        if (readType == "bool") {
            this.state[name] = value.getUint8(0) == 1;
        } else if (readType == "number") {
            this.state[name] = value.getUint8(0);
        } else if (readType == "string") {
            this.state[name] = new TextDecoder().decode(value);
        } else if (readType == "string_lower") {
            this.state[name] = new TextDecoder().decode(value).toLowerCase();
        }
        this.onStateChange(name, this.state, changeType);
    }

    scan() {
        return new Promise((resolve, reject) => {
            navigator.bluetooth
                .requestDevice({
                    filters: [{ manufacturerData: [{ companyIdentifier: 2833 }] }],
                    optionalServices: ["device_information", "00010074-6865-726d-6f77-6f726b730d0a", "00ff0074-6865-726d-6f77-6f726b730d0a"],
                })
                .then((device) => {
                    if (device) {
                        this.device = device;

                        device.ongattserverdisconnected = (e) => {
                            //console.log(e);
                            //console.log("Device disconnected");
                            this.onProgressChange("Device disconnected.");
                            this.onDisconnect();
                            this.cleanup();
                        };

                        this.onProgressChange("Connecting to device.");
                        return device.gatt.connect();
                    } else {
                        reject("No device selected.");
                    }
                })
                .then((gattServer) => {
                    this.onProgressChange("Requesting device information.");
                    return gattServer.getPrimaryServices();
                })
                .then(async (services) => {
                    let oustandingRequests = [];

                    for (const service of services) {
                        //console.log(service.uuid);

                        const serviceConfigs = this.characteristicsByService[service.uuid];
                        if (serviceConfigs) {
                            for (const configuration of serviceConfigs) {
                                oustandingRequests.push(
                                    service.getCharacteristic(configuration.characteristicUUID).then((characteristic) => {
                                        configuration.characteristic = characteristic;

                                        if (configuration.notify) {
                                            characteristic.oncharacteristicvaluechanged = (e) => {
                                                // console.log(`notification for ${configuration.name}`);
                                                this.setStateValue(configuration.name, e.target.value, configuration.readType, "notify");
                                            };
                                            characteristic.startNotifications();
                                        }

                                        if (configuration.readValue) {
                                            // console.log(`reading value of ${configuration.name}`)
                                            return characteristic.readValue()
                                                        .then((value) => this.setStateValue(configuration.name, value, configuration.readType, "read"))
                                                        .catch((err) => console.error(`Unable to read value ${configuration.name}: ${err}`));
                                        } else {
                                            return new Promise((resolve, _) => resolve());
                                        }
                                    })
                                );
                            }
                        }
                    }

                    try {
                        await Promise.all(oustandingRequests);
                        this.characteristics.forEach((characteristic) => {
                            if (!characteristic.characteristic) {
                                console.error(`No characteristic for ${characteristic.name}`);
                            }
                        })
                        resolve({ bleContext: this });
                    } catch (err) {
                        reject(err);
                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    async writeChunks(characteristic, value) {
        const chunkSize = 128;
        const svalue = value
            .replace("-----BEGIN RSA PRIVATE KEY-----", "-----BEGIN PRIVATE KEY-----")
            .replace("-----END RSA PRIVATE KEY-----", "-----END PRIVATE KEY-----");
        //console.log(svalue);
        const totalChunks = svalue.length / chunkSize;
        for (let chunk=0; chunk < totalChunks; chunk++) {
            const startIndex = chunk * chunkSize;
            const endIndex = (chunk + 1) * chunkSize > svalue.length ? svalue.length : (chunk + 1) * chunkSize;
            const sv = svalue.substring(startIndex, endIndex);
            await characteristic.writeValue(new TextEncoder().encode(sv));
        }
    }

    async writeImediate(value, characteristic, characteristicName) {
        try {
            if (value) {
                if (value.length > 100) {
                    //console.log(`this.writeChunks(${characteristicName}, "${value}");`);
                    await this.writeChunks(characteristic, value);
                } else {
                    //console.log(`await characteristic.${characteristicName}.writeValue(new TextEncoder().encode("${value}"));`);
                    await characteristic.writeValue(new TextEncoder().encode(value));
                }
            } else {
                //console.log(`await characteristic.${characteristicName}.writeValue(new TextEncoder().encode(""));`);
                await characteristic.writeValue(new TextEncoder().encode(""));
            }
        } catch(err) {
            console.error(`Error in writeImediate for ${characteristicName}`);
            console.error(err);
        }
    }

    executeCommand(commandName, commandCallback) {
        return new Promise((resolve, reject) => {
            //console.log("executeCommand start");

            const commandCharacteristic = this.characteristicsByName["command"]?.characteristic;
            if (commandCharacteristic) {
                if (commandCallback) {
                    commandCharacteristic.oncharacteristicvaluechanged = (e) => commandCallback(new TextDecoder().decode(e.target.value));
                    commandCharacteristic.startNotifications();
                }

                commandCharacteristic
                    .writeValue(new TextEncoder().encode(commandName))
                    .then(() => resolve())
                    .catch((err) => {
                        console.error(err);

                        if (commandCallback) {
                            commandCharacteristic.stopNotifications();
                        }

                        reject(err);
                    });
            } else {
                reject("No command characteristic found.");
            }

            //console.log("executeCommand end");
        });
    }

    execute() {
        return new Promise((resolve, reject) => {
            //console.log("execute start");

            if (!this.isConnected()) {
                reject("Not connected");
                return;
            }

            try {
                const waitOnWriteValues = [];

                for (const characteristic of this.characteristics) {
                    if (characteristic.writeType == "string" && characteristic.autoWrite) {
                        if (characteristic.next == null) {
                            this.writeImediate(this.writeValues[characteristic.name], characteristic.characteristic, characteristic.name);
                        } else {
                            characteristic.executeWrite = () => {
                                //console.log(`nextWriteFunction ${characteristic.name}`);
                                this.writeImediate(this.writeValues[characteristic.name], characteristic.characteristic, characteristic.name);
                                if (characteristic.name == "wifi_password") {
                                    //console.log("sending start");
                                    const commandConfig = this.characteristicsByName["command"];
                                    if (commandConfig) {
                                        setTimeout(() => this.writeImediate("CONNECT_START", commandConfig.characteristic, "command"), 1000);
                                    }
                                }
                            };
                            waitOnWriteValues.push(characteristic);
                        }
                    }
                }

                if (waitOnWriteValues.length > 0) {
                    waitOnWriteValues[0].executeWrite();

                    var count = 0;
                    var index = 0;
                    this.timer = setInterval(() => {
                        const runNext = waitOnWriteValues[index].next(this.state);

                        //console.log(`runNext=${runNext}`);
                        //console.log(this.state);

                        if (runNext != null) {
                            index++;
                            if (runNext == "_complete") {
                                clearInterval(this.timer);
                                resolve();
                            } else {
                                //console.log(`running next ${waitOnWriteValues[index].name}`);
                                waitOnWriteValues[index].executeWrite();
                            }
                        }

                        count++;
                        if (count > 60) {
                            //console.error("Timeout waiting for setup to complete.");
                            clearInterval(this.timer);
                            reject("Timeout waiting for setup to complete.");
                        }
                    }, 1000);
                } else {
                    resolve();
                }

                //console.log("execute end");
            } catch(err) {
                console.error("Error in execute");
                console.error(err);
                reject(err);
            }
        });
    }

    cleanup() {
        if (this.cleanupRunning) {
            return;
        }

        this.cleanupRunning = true;
        //console.log('Cleanup start')

        try {
            if (this.timer != null) {
                clearInterval(this.timer);
            }

            if (this.isConnected()) {
                this.characteristics.forEach((characteristic) => {
                    if (characteristic.notify) {
                        //console.log("notify cleanup");
                        characteristic.characteristic.stopNotifications().catch((err) => console.log(err));
                    }
                });
                // Give the above stop notifications a chance to complete before disconnecting
                const device = this.device;
                setTimeout(() => device.gatt.disconnect(), 1000);
            }

            this.characteristicsByName = {};
            this.characteristicsByService = {};
            this.characteristics = [];
            this.state = {};

            if (this.device != null) {
                this.device = null;
            }
        } catch(err) {
            console.error(err);
        }

        //console.log("Cleanup end");
        this.cleanupRunning = false;
    }

    isConnected() {
        return this.device != null && this.device.gatt.connected;
    }
};

class BLEService {
    static canSupportBLECalls() {
        return navigator.bluetooth;
    }

    static applyConfiguration(bleContext) {
        return bleContext.execute();
    }

    static sendWiFiScan(bleContext, onScanResultAdded) {
        return bleContext.executeCommand("SCAN", onScanResultAdded);        
    }

    static sendFactoryReset(bleContext) {
        return bleContext.executeCommand("FACTORY_RESET");
    }

    static sendLogLevelCommand(bleContext) {
        return bleContext.executeCommand("LOG_LEVEL");
    }

    static startScan(onProgressChange, onStateChange, onDisconnect) {
        const context = new BLEContext();

        context.onProgressChange = onProgressChange;
        context.onDisconnect = onDisconnect;
        context.onStateChange = onStateChange;

        /*
        config values = {
            name: name,
            serviceUUID: serviceUUID,
            characteristicUUID: characteristicUUID,
            writeType: writeType,
            autoWrite: autoWrite,
            readValue: readValue,
            readType: readType,
            notify: notify,
            next: next,
        }
        */
        context.configure({
            name: "firmware",
            serviceUUID: "0000180a-0000-1000-8000-00805f9b34fb",
            characteristicUUID: "firmware_revision_string",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "hardware",
            serviceUUID: "0000180a-0000-1000-8000-00805f9b34fb",
            characteristicUUID: "hardware_revision_string",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "software",
            serviceUUID: "0000180a-0000-1000-8000-00805f9b34fb",
            characteristicUUID: "software_revision_string",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "model",
            serviceUUID: "0000180a-0000-1000-8000-00805f9b34fb",
            characteristicUUID: "model_number_string",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "manufacturer",
            serviceUUID: "0000180a-0000-1000-8000-00805f9b34fb",
            characteristicUUID: "manufacturer_name_string",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });

        context.configure({
            name: "device_id",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010c74-6865-726d-6f77-6f726b730d0a",
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "mqtt_broker_url",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010d74-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "mqtt_broker_port",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010e74-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string_lower",
            notify: false,
        });
        context.configure({
            name: "mqtt_username",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010f74-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string",
            notify: false,
        });
        context.configure({
            name: "mqtt_password",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00011074-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: false,
            notify: false,
        });
        context.configure({
            name: "private_key",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010374-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: false,
            notify: false,
        });

        context.configure({
            name: "command",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010874-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: false,
            readValue: false,
            notify: false,
        });

        context.configure({
            name: "wifi_status",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010474-6865-726d-6f77-6f726b730d0a",
            readValue: true,
            readType: "bool",
            notify: true,
        });
        context.configure({
            name: "wifi_error",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010574-6865-726d-6f77-6f726b730d0a",
            readValue: true,
            readType: "number",
            notify: true,
        });
        context.configure({
            name: "mqtt_status",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010674-6865-726d-6f77-6f726b730d0a",
            readValue: true,
            readType: "bool",
            notify: true,
        });
        context.configure({
            name: "mqtt_error",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010774-6865-726d-6f77-6f726b730d0a",
            readValue: true,
            readType: "number",
            notify: true,
        });

        context.configure({
            name: "wifi_ssid",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010174-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string",
            notify: false,
        });

        // The order here defines what is written first
        context.configure({
            name: "mqtt_ca_cert",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00011174-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string",
            notify: true,
            next: (state) => (state["mqtt_ca_cert"] == "VALID" ? "mqtt_client_cert" : null),
        });
        context.configure({
            name: "mqtt_client_cert",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00011274-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string",
            notify: true,
            next: (state) => (state["mqtt_client_cert"] == "VALID" ? "mqtt_client_key" : null),
        });
        context.configure({
            name: "mqtt_client_key",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00011374-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: true,
            readType: "string",
            notify: true,
            next: (state) => (state["mqtt_client_key"] == "VALID" ? "wifi_password" : null),
        });
        context.configure({
            name: "wifi_password",
            serviceUUID: "00010074-6865-726d-6f77-6f726b730d0a",
            characteristicUUID: "00010274-6865-726d-6f77-6f726b730d0a",
            writeType: "string",
            autoWrite: true,
            readValue: false,
            notify: false,
            next: (state) => (state["mqtt_status"] ? "_complete" : null),
        });

        return context.scan(onProgressChange, onDisconnect);
    }
}

export default BLEService;
